import { useChatMessages } from '@ai-sdk-tools/store';
import type { ComponentType, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ChatDisclaimer } from './chat-disclaimer';
import { ChatInputContainer, OutlookConnectPrompt } from './chat-input';
import { ChatSettingsPanel } from './chat-settings-panel';
import { CreateSkillView } from './custom-skills-ui';
import { Messages, type PreviewMessageProps } from './messages';
import { NovaAlert } from './nova-alert';
import { OutlookManageView } from './outlook-connect-settings';
import { SettingsPanelContext, type SettingsPanelCloseGuard, type SettingsPanelView } from './settings-panel-context';
import { SuggestedPrompts } from './suggested-prompts';
import { ArtifactPreviewProvider } from './tools/artifact-preview/artifact-preview-provider';
import { getLatestPendingClarification } from '../../../lib/clarification';
import type { UIMessageWithMetadata } from '../../../lib/types';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { SkillsProvider } from '../contexts/skills-context';
import type { DataPartRenderer, ToolOutputRenderer } from '../lib/default-renderers';

interface ChatContentProps {
  // Pass through all props that Messages component expects
  chatUuid?: string;
  className?: string;
  isLoading?: boolean;
  /**
   * Object mapping tool names to their renderer functions.
   * Keys are tool names (e.g., 'processFiles', 'image_generation').
   * Values are renderer functions that take the part and messageUuid.
   */
  toolRenderers?: Record<string, ToolOutputRenderer>;
  /**
   * Object mapping data part types to their renderer functions.
   * Keys are data part types (e.g., 'data-coverageGapAnalysis').
   * Values are renderer functions that take the part and message.
   */
  dataPartRenderers?: Record<string, DataPartRenderer>;
  /**
   * Custom message renderer component.
   * If provided, this component will be used instead of the default PreviewMessage.
   * Must accept the same props as PreviewMessage.
   */
  messageRenderer?: ComponentType<PreviewMessageProps>;
  /**
   * Controls whether inline citations and sources are rendered.
   * Passed through to Messages component.
   */
  enableSourcesRendering?: boolean;
  // Props for input container
  placeholder?: string;
  toolbar?: ReactNode;
  /**
   * Most embedded Nova surfaces only render ChatContent, so they need a local artifact
   * preview provider. The standalone Nova shell mounts a layout-level provider instead
   * so the preview can push the chat content instead of overlaying messages.
   */
  withArtifactPreviewProvider?: boolean;
}

/**
 * Content wrapper component that decides between rendering Messages+Input or Settings panel.
 * Keeps Messages component unchanged and focused on its single responsibility.
 *
 * When settings is open, displays only the ChatSettingsPanel.
 * When settings is closed, displays the Messages component with input and disclaimer.
 */
export function ChatContent({
  placeholder,
  toolbar,
  withArtifactPreviewProvider = true,
  ...messagesProps
}: ChatContentProps) {
  const {
    isSettingsOpen,
    setIsSettingsOpen,
    settingsBackSignal,
    settingsCloseIntentRef,
    setHasActiveSettingsSubView,
    currentChatAppType,
    outlookConnectEnabled,
    outlookNeedsReauth,
    reconnectOutlook,
    canConnectOutlook,
    isWtwColleague,
  } = useChatOverlayContext();
  const messages = useChatMessages<UIMessageWithMetadata>();
  const [activeView, setActiveView] = useState<SettingsPanelView | null>(null);
  const [isOutlookSessionAlertDismissed, setIsOutlookSessionAlertDismissed] = useState(false);
  const backHandlerRef = useRef<SettingsPanelCloseGuard | null>(null);
  const initialSettingsBackSignalRef = useRef(settingsBackSignal);

  const isOutlookChatWithoutConnect = canConnectOutlook && currentChatAppType === 'OUTLOOK' && !outlookConnectEnabled;
  const hasPendingClarification = !!getLatestPendingClarification(messages);
  const messagesNode = <Messages {...messagesProps} />;

  useEffect(() => {
    if (!outlookNeedsReauth) {
      setIsOutlookSessionAlertDismissed(false);
    }
  }, [outlookNeedsReauth]);

  const closeSettingsSubView = useCallback(
    (afterClose?: () => void) => {
      backHandlerRef.current = null;
      setActiveView(null);
      setHasActiveSettingsSubView(false);

      if (afterClose) {
        setIsSettingsOpen(false);
        afterClose();
      }
    },
    [setHasActiveSettingsSubView, setIsSettingsOpen],
  );

  useEffect(() => {
    return () => {
      backHandlerRef.current = null;
      setHasActiveSettingsSubView(false);
    };
  }, [setHasActiveSettingsSubView]);

  useEffect(() => {
    if (settingsBackSignal === initialSettingsBackSignalRef.current) return;
    initialSettingsBackSignalRef.current = settingsBackSignal;

    const afterClose = settingsCloseIntentRef.current ?? undefined;
    settingsCloseIntentRef.current = null;

    if (activeView) {
      const backHandler = backHandlerRef.current;
      if (backHandler) {
        void Promise.resolve(backHandler()).then((canClose) => {
          if (canClose !== false) {
            closeSettingsSubView(afterClose);
          }
        });
      } else {
        closeSettingsSubView(afterClose);
      }
      return;
    }

    if (isSettingsOpen) {
      setIsSettingsOpen(false);
      afterClose?.();
    }
  }, [activeView, closeSettingsSubView, isSettingsOpen, setIsSettingsOpen, settingsBackSignal, settingsCloseIntentRef]);

  return (
    <SkillsProvider>
      <SettingsPanelContext.Provider
        value={useMemo(
          () => ({
            pushView: (view: SettingsPanelView) => {
              backHandlerRef.current = null;
              setActiveView(view);
              setHasActiveSettingsSubView(true);
            },
            popView: () => closeSettingsSubView(),
            backHandlerRef,
          }),
          [closeSettingsSubView, setHasActiveSettingsSubView],
        )}
      >
        {activeView ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <SettingsSubView view={activeView} onBack={() => closeSettingsSubView()} />
          </div>
        ) : isSettingsOpen ? (
          <ChatSettingsPanel />
        ) : (
          <>
            <div className="relative flex flex-1 flex-col overflow-hidden">
              {canConnectOutlook && outlookNeedsReauth && !isOutlookSessionAlertDismissed && (
                <div className="absolute top-4 left-1/2 z-20 -translate-x-1/2">
                  <NovaAlert
                    variant="warning"
                    message="Outlook session expired."
                    action={{ label: 'Reconnect', onClick: reconnectOutlook }}
                    onDismiss={() => setIsOutlookSessionAlertDismissed(true)}
                  />
                </div>
              )}
              {withArtifactPreviewProvider ? (
                <ArtifactPreviewProvider>{messagesNode}</ArtifactPreviewProvider>
              ) : (
                messagesNode
              )}
            </div>
            {isOutlookChatWithoutConnect ? (
              <OutlookConnectPrompt />
            ) : hasPendingClarification ? null : (
              <ChatInputContainer
                placeholder={placeholder}
                toolbar={toolbar}
                topToolBar={<SuggestedPrompts maxRows={2} />}
              />
            )}
            <ChatDisclaimer isWtwColleague={isWtwColleague} />
          </>
        )}
      </SettingsPanelContext.Provider>
    </SkillsProvider>
  );
}

function SettingsSubView({ view, onBack }: { view: SettingsPanelView; onBack: () => void }) {
  switch (view.type) {
    case 'outlook-manage':
      return <OutlookManageView onBack={onBack} />;
    case 'custom-skill-create':
      return <CreateSkillView onBack={onBack} />;
    case 'custom-skill-edit':
      return <CreateSkillView onBack={onBack} skillToUpdate={view.skill} />;
  }
}
